/**
 * Online play wire protocol (JSON text frames over one WebSocket), shared by the browser client
 * and the relay server (`server/`). Pure TypeScript: no DOM and no Node APIs.
 *
 * Model (04_NETCODE.md): both clients run the same deterministic sim; the server forms rooms,
 * picks the seed, relays each player's per-tick inputs to the other, keeps the input log (for
 * rejoining and desync analysis) and compares world hashes. It never simulates.
 */

import { MAPS, type MapKey } from "../game/assets";
import type { ControlState } from "../sim/controls";
import { type MapDef, parseCustomMap } from "../sim/maps";
import { type MatchRules, normalizeRules } from "../sim/match-rules";

/** Bumped on any incompatible change; an installed (cached PWA) client then gets a clear error. */
export const PROTOCOL_VERSION = 4;

/** Modes an online room can play (P11.2); survival is single-player. */
export type OnlineMode = "duel" | "koth" | "ctf";
const ONLINE_MODES: readonly OnlineMode[] = ["duel", "koth", "ctf"];
/** Most ticks one input message may carry. */
export const MAX_INPUTS_PER_MESSAGE = 64;
/** Largest time-sync advantage a client may report (ticks). */
export const MAX_ADVANTAGE = 1000;
/** Path of the WebSocket endpoint, relative to the page (nginx and Vite proxy it to the server). */
export const SOCKET_PATH = "ws";

/** A seat in an online room: two players, or up to four in a party room (P11.3). */
export type PlayerSlot = 0 | 1 | 2 | 3;

/** Everything both clients need to build the identical world. */
export interface MatchSetup {
	code: string;
	map: MapKey;
	rules: MatchRules;
	seed: number;
	/** Ticks of input delay both clients use (P10.5, from their round trips). */
	inputDelay: number;
	/** The host's own map (P11.1); validated by the server; replaces `map` when present. */
	customMap?: MapDef;
	/** Duel (default), King of the Hill or Capture the Flag. */
	mode?: OnlineMode;
	/** Seats in the room: 2 (default) or a 3–4 pilot party room (P11.3). */
	players?: number;
	/** Pilot names by slot ("" = none, P11.8); absent when nobody set one. */
	names?: string[];
}

/** A new map, rules and mode the host picked for the rematch (I-013); the others must accept it. */
export interface RematchSetup {
	map: MapKey;
	rules: MatchRules;
	customMap?: MapDef;
	mode?: OnlineMode;
}

/** The setup with the host's rematch choice applied (a duel and a built-in map leave no field). */
export function withRematchSetup(setup: MatchSetup, next: RematchSetup): MatchSetup {
	const { customMap: _map, mode: _mode, ...rest } = setup;
	return {
		...rest,
		map: next.map,
		rules: next.rules,
		...(next.customMap ? { customMap: next.customMap } : {}),
		...(next.mode && next.mode !== "duel" ? { mode: next.mode } : {}),
	};
}

/** Longest pilot name (P11.8; also the highscore name). */
export const PILOT_NAME_MAX = 12;

/**
 * A pilot name from untrusted text: whitespace collapsed, control and format characters
 * (zero-width, bidi overrides) removed, trimmed and cut to PILOT_NAME_MAX. "" = no name (the ship
 * colour is shown).
 */
export function cleanPilotName(raw: unknown): string {
	if (typeof raw !== "string") return "";
	return raw
		.replace(/\s+/g, " ")
		.replace(/[\p{Cc}\p{Cf}]/gu, "")
		.trim()
		.slice(0, PILOT_NAME_MAX)
		.trim();
}

function nameField(raw: unknown): { name?: string } {
	const name = cleanPilotName(raw);
	return name ? { name } : {};
}

/** Input delay bounds (ticks) and the default when a round trip is unknown. */
export const MIN_INPUT_DELAY = 1;
export const MAX_INPUT_DELAY = 4;
export const DEFAULT_INPUT_DELAY = 2;
/** Longest round trip a client may report (ms). */
export const MAX_RTT_MS = 5000;

/**
 * Input delay for two players' round trips to the server (P10.5). An input travels
 * player → server → player, about half of each round trip; the delay hides roughly half of
 * that (rollback covers the rest): ≤ 30 ms → 1 tick, ~60 ms → 2, ~120 ms+ → 4.
 */
export function inputDelayFor(
	rttA: number | null | undefined,
	rttB: number | null | undefined,
): number {
	if (rttA == null || rttB == null) return DEFAULT_INPUT_DELAY;
	const oneWay = rttA / 2 + rttB / 2;
	const ticks = Math.round(oneWay / 2 / 15);
	return Math.min(MAX_INPUT_DELAY, Math.max(MIN_INPUT_DELAY, ticks));
}

/** Just the match setup out of a `start` / `resume` message. */
export function setupOf(m: MatchSetup): MatchSetup {
	return {
		code: m.code,
		map: m.map,
		rules: m.rules,
		seed: m.seed,
		inputDelay: m.inputDelay,
		...(m.customMap ? { customMap: m.customMap } : {}),
		...(m.mode ? { mode: m.mode } : {}),
		...(m.players ? { players: m.players } : {}),
		...(m.names ? { names: [...m.names] } : {}),
	};
}

function isRtt(v: unknown): v is number {
	return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= MAX_RTT_MS;
}

export type ClientMessage =
	/** `rtt`: the client's measured round trip to the server in ms (input delay, P10.5). */
	| {
			t: "create";
			v: number;
			map: MapKey;
			rules: MatchRules;
			rtt?: number;
			customMap?: MapDef;
			mode?: OnlineMode;
			/** Room size, 2–4 (P11.3). */
			players?: number;
			/** The pilot's name (P11.8). */
			name?: string;
	  }
	| { t: "join"; v: number; code: string; rtt?: number; name?: string }
	| { t: "rejoin"; v: number; code: string; token: string }
	/**
	 * Inputs for ticks `start .. start + bits.length - 1` (may overlap what was sent before).
	 * `adv`: how many ticks the sender runs ahead of the inputs it has from the other player
	 * (time sync, relayed as is).
	 */
	| { t: "input"; start: number; bits: number[]; adv?: number }
	| { t: "hash"; tick: number; hash: string }
	| { t: "ping"; id: number }
	/**
	 * After a match: ask for (or accept) another one in the same room. The host (seat 0) may send
	 * a new `setup`; everyone else then has to ask again for that one (I-013).
	 */
	| { t: "rematch"; rtt?: number; setup?: RematchSetup }
	| { t: "leave" };

export type ErrorCode =
	| "bad-message"
	| "version"
	| "server-full"
	| "room-not-found"
	| "room-full"
	| "already-in-room"
	| "rejoin-failed";

export type EndReason = "peer-left" | "peer-timeout" | "shutdown" | "too-long";

export type ServerMessage =
	| { t: "created"; code: string }
	| ({ t: "start"; player: PlayerSlot; token: string } & MatchSetup)
	/** Rejoin: the full input log of both players so the client can re-simulate from tick 0. */
	| ({ t: "resume"; player: PlayerSlot; token: string; inputs: number[][] } & MatchSetup)
	/** Party rooms: seats taken so far while waiting for the room to fill. */
	| { t: "lobby"; joined: number; size: number }
	| { t: "input"; player: PlayerSlot; start: number; bits: number[]; adv?: number }
	| { t: "peer-left"; graceMs: number }
	| { t: "peer-back" }
	/**
	 * Someone asked for a rematch. Everyone asking → a new `start` (fresh seed). `setup`: the host
	 * picked a new map, rules or mode, and any earlier request was cleared (I-013).
	 */
	| { t: "rematch-wanted"; setup?: RematchSetup }
	| { t: "desync"; tick: number }
	| { t: "ended"; reason: EndReason }
	| { t: "error"; code: ErrorCode }
	| { t: "pong"; id: number };

const THRUST = 1;
const LEFT = 2;
const RIGHT = 4;
const LASER = 8;
const MISSILE = 16;
const ALL_BITS = 31;

/** One tick of controls as a 5-bit number. */
export function packControls(c: Readonly<ControlState>): number {
	return (
		(c.thrust ? THRUST : 0) |
		(c.left ? LEFT : 0) |
		(c.right ? RIGHT : 0) |
		(c.fireLaser ? LASER : 0) |
		(c.fireMissile ? MISSILE : 0)
	);
}

export function unpackControls(bits: number): ControlState {
	return {
		thrust: (bits & THRUST) !== 0,
		left: (bits & LEFT) !== 0,
		right: (bits & RIGHT) !== 0,
		fireLaser: (bits & LASER) !== 0,
		fireMissile: (bits & MISSILE) !== 0,
	};
}

const ROOM_CODE = /^[A-Z2-9]{4}$/;

function isRecord(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isTick(v: unknown): v is number {
	return Number.isSafeInteger(v) && (v as number) >= 0;
}

function isInputBits(v: unknown): v is number[] {
	return (
		Array.isArray(v) &&
		v.length > 0 &&
		v.length <= MAX_INPUTS_PER_MESSAGE &&
		v.every((b) => Number.isInteger(b) && b >= 0 && b <= ALL_BITS)
	);
}

function isMapKey(v: unknown): v is MapKey {
	return typeof v === "string" && Object.hasOwn(MAPS, v);
}

/** Map, rules and mode of a `create` or a rematch `setup`; `null` = malformed. */
function parseRematchSetup(m: unknown): RematchSetup | null {
	if (!isRecord(m) || !isMapKey(m.map) || !isRecord(m.rules)) return null;
	let customMap: MapDef | undefined;
	if (m.customMap !== undefined) {
		try {
			customMap = parseCustomMap(m.customMap);
		} catch {
			return null;
		}
	}
	return {
		map: m.map,
		rules: normalizeRules(m.rules),
		...(customMap ? { customMap } : {}),
		...(ONLINE_MODES.includes(m.mode as OnlineMode) ? { mode: m.mode as OnlineMode } : {}),
	};
}

/** Validates an untrusted client frame; `null` = malformed. Rules are normalised, not trusted. */
export function parseClientMessage(text: string): ClientMessage | null {
	let m: unknown;
	try {
		m = JSON.parse(text);
	} catch {
		return null;
	}
	if (!isRecord(m)) return null;
	switch (m.t) {
		case "create": {
			const setup = parseRematchSetup(m);
			if (!Number.isInteger(m.v) || !setup) return null;
			return {
				t: "create",
				v: m.v as number,
				...setup,
				...(isRtt(m.rtt) ? { rtt: m.rtt } : {}),
				...(m.players === 3 || m.players === 4 ? { players: m.players } : {}),
				...nameField(m.name),
			};
		}
		case "join":
			return Number.isInteger(m.v) && typeof m.code === "string" && ROOM_CODE.test(m.code)
				? {
						t: "join",
						v: m.v as number,
						code: m.code,
						...(isRtt(m.rtt) ? { rtt: m.rtt } : {}),
						...nameField(m.name),
					}
				: null;
		case "rejoin":
			return Number.isInteger(m.v) &&
				typeof m.code === "string" &&
				ROOM_CODE.test(m.code) &&
				typeof m.token === "string" &&
				m.token.length <= 64
				? { t: "rejoin", v: m.v as number, code: m.code, token: m.token }
				: null;
		case "input":
			if (!isTick(m.start) || !isInputBits(m.bits)) return null;
			if (m.adv === undefined) return { t: "input", start: m.start, bits: m.bits };
			return Number.isInteger(m.adv) && Math.abs(m.adv as number) <= MAX_ADVANTAGE
				? { t: "input", start: m.start, bits: m.bits, adv: m.adv as number }
				: null;
		case "hash":
			return isTick(m.tick) && typeof m.hash === "string" && m.hash.length <= 16
				? { t: "hash", tick: m.tick, hash: m.hash }
				: null;
		case "ping":
			return Number.isSafeInteger(m.id) ? { t: "ping", id: m.id as number } : null;
		case "leave":
			return { t: "leave" };
		case "rematch": {
			const setup = m.setup === undefined ? undefined : parseRematchSetup(m.setup);
			if (setup === null) return null;
			return {
				t: "rematch",
				...(isRtt(m.rtt) ? { rtt: m.rtt } : {}),
				...(setup ? { setup } : {}),
			};
		}
		default:
			return null;
	}
}

/**
 * Parses a server frame. The server is trusted to send well-formed messages, so this only
 * guards against garbage (wrong endpoint, proxy error pages); `null` = not a protocol message.
 */
export function parseServerMessage(text: string): ServerMessage | null {
	try {
		const m: unknown = JSON.parse(text);
		return isRecord(m) && typeof m.t === "string" ? (m as ServerMessage) : null;
	} catch {
		return null;
	}
}

const URL_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * A usable custom server address (empty = this site): `host[:port][/path]` or a ws(s)/http(s) URL
 * whose host is `localhost`, an IP or a dotted name. A bare word ("Test") is refused: it was typed
 * as a pilot name and made every connection fail (I-023).
 */
export function isServerAddress(raw: string): boolean {
	const text = raw.trim();
	if (!text) return true;
	if (/\s/.test(text)) return false;
	let url: URL;
	try {
		url = new URL(URL_SCHEME.test(text) ? text : `wss://${text}`);
	} catch {
		return false;
	}
	if (!["ws:", "wss:", "http:", "https:"].includes(url.protocol)) return false;
	if (url.username || url.password) return false;
	const host = url.hostname;
	return host === "localhost" || host.startsWith("[") || /^[^.]+(\.[^.]+)+$/.test(host);
}

/**
 * WebSocket URL of the relay for a page URL: same origin and path, `ws:` or `wss:`. A custom
 * server (settings) may be given as `host[:port]`, `ws(s)://…` or `http(s)://…`.
 */
export function socketUrl(pageUrl: string, custom?: string): string {
	if (custom?.trim()) {
		const raw = custom.trim();
		const withScheme = URL_SCHEME.test(raw) ? raw : `wss://${raw}`;
		const url = new URL(withScheme);
		if (url.protocol === "http:") url.protocol = "ws:";
		if (url.protocol === "https:") url.protocol = "wss:";
		if (url.pathname === "/" || url.pathname === "") url.pathname = `/${SOCKET_PATH}`;
		return url.toString();
	}
	const url = new URL(SOCKET_PATH, pageUrl);
	url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
	url.search = "";
	url.hash = "";
	return url.toString();
}
